import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Bell, CalendarClock, Flag, Radar as RadarIcon, Users } from 'lucide-react';
import type { MissionSummary } from '@missions/types';
import { Button } from '@missions/ui';
import { Av, Due, Empty, Pri, Radar, SDot } from '../components/atoms';
import { useApi } from '../lib/hooks';
import { setPageTitle } from '../lib/seo';
import { useSession, useT } from '../lib/session';
import { useChrome, useShell } from '../layout/shell-context';
import { GoToCard, OnThisPage, ProjectTree } from '../layout/sidebar-parts';
import { NotificationFeed } from '../features/notifications/NotificationList';

export function MissionRow({ m, projectName, projectColor }: { m: MissionSummary; projectName?: string; projectColor?: string }) {
  const shell = useShell();
  return (
    <button className="lrow" onClick={() => shell.openMission(m.id)} style={{ padding: '12px 16px' }}>
      <SDot s={{ name: m.completedAt ? 'Done' : 'Open', color: '#8a94a6', isClosed: !!m.completedAt }} />
      <span className="nm">
        {m.title}
        <small>
          <span className="tid" style={{ marginRight: 6 }}>{m.key}</span>
          {projectColor && <span className="dot" style={{ background: projectColor, marginRight: 6 }} />}
          {projectName ? `${projectName} · ` : ''}
          {m.etaMinutes ? `${Math.round((m.etaMinutes / 60) * 10) / 10}h ETA` : 'No ETA'}
        </small>
      </span>
      <span style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 'none' }}>
        <Pri p={m.priority} compact />
        <Due due={m.dueAt} done={!!m.completedAt} />
      </span>
    </button>
  );
}

/** Mission Control (Home): Today's briefing, active projects, deadlines, crew workload, latest updates. */
export function MissionControl() {
  const t = useT();
  const s = useSession();
  const nav = useNavigate();
  const shell = useShell();
  const q = useApi('missionControl.get', { query: { workspaceId: shell.workspaceId || undefined } });
  useEffect(() => setPageTitle(t.home), [t.home]);
  useChrome(
    {
      section: 'Work',
      current: <><RadarIcon style={{ color: 'var(--text-3)' }} /><span className="nm">{t.home}</span></>,
      secondaryTitle: t.home,
      secondary: (
        <>
          <div className="glabel" style={{ paddingTop: 4 }}>All projects</div>
          <ProjectTree />
        </>
      ),
      rail: (
        <>
          <OnThisPage links={[['h-stats', 'Today’s briefing'], ['h-missions', 'Active projects'], ['h-due', 'Upcoming deadlines'], ['h-crew', `${t.crew} workload`], ['h-notif', 'Latest updates']]} />
          <GoToCard />
        </>
      ),
    },
    [t],
  );
  const d = q.data;
  const ld = q.isPending;
  const hr = new Date().getHours();
  const first = s.me?.user.name.split(' ')[0] ?? '';
  const dateMono = new Date().toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
  const cn = (k: string, v: string | number, c?: string) => (
    <div className="cn" key={k}>
      <small>{k}</small>
      <b style={c ? { color: c } : undefined}>{ld ? <span className="ui-skel" style={{ width: 40, height: 22, display: 'inline-block' }} /> : v}</b>
    </div>
  );
  const planned = Math.round((d?.briefing.plannedMinutes ?? 0) / 60);
  const maxLoad = Math.max(40 * 60, ...(d?.workload.map((w) => w.plannedMinutes) ?? [0]));
  const projById = new Map(d?.activeProjects.map((p) => [p.id, p]) ?? []);

  if (q.isError)
    return (
      <div className="page">
        <div className="card"><Empty title="Couldn’t load your briefing" text="Check your connection and try again." actions={<Button onClick={() => void q.refetch()}>Try again</Button>} /></div>
      </div>
    );

  return (
    <div className="page" id="pgHome">
      <div className="brief ms-grid-bg" id="h-stats" style={{ scrollMarginTop: 12 }}>
        <div className="radar-corner"><Radar /></div>
        <div className="eyebrow">{t.tMinusDates ? 'Today’s briefing' : 'Today'} · {dateMono}</div>
        <h1>Good {hr < 12 ? 'morning' : hr < 17 ? 'afternoon' : 'evening'}, {first}</h1>
        <p>
          {d && d.briefing.dueToday + d.briefing.overdue === 0 ? (
            <>Nothing urgent today. {planned ? <><b>{planned}h</b> of work is planned this week.</> : null}</>
          ) : d ? (
            <>
              You have <b>{d.briefing.dueToday} due today</b> and <b style={{ color: 'var(--danger)' }}>{d.briefing.overdue} overdue</b>, with <b>{planned}h</b> of work planned this week.
            </>
          ) : (
            ' '
          )}
        </p>
        <div className="cnts">
          {cn('Due today', d?.briefing.dueToday ?? 0, 'var(--brand)')}
          {cn('Overdue', d?.briefing.overdue ?? 0, 'var(--danger)')}
          {cn('Mentions', d?.briefing.mentions ?? 0)}
          {cn('Planned hours', `${planned}h`)}
        </div>
      </div>

      <div className="card" id="h-missions" style={{ marginTop: 16, scrollMarginTop: 12 }}>
        <div className="card-h">
          <span className="tile"><Flag /></span>
          <h2>Active projects</h2>
          <span className="sub">{d?.activeProjects.length ?? ''}</span>
          <span className="sp" />
          <button className="btn btn-g sm" onClick={() => nav('/projects')}>Open <ArrowRight /></button>
        </div>
        {ld ? (
          <div className="missions">
            {[0, 1, 2].map((i) => (
              <div className="mcard" key={i}><span className="ui-skel" style={{ width: '70%', height: 14, display: 'block' }} /><span className="ui-skel" style={{ width: '40%', height: 12, display: 'block' }} /></div>
            ))}
          </div>
        ) : !d?.activeProjects.length ? (
          <Empty title={t.emptyList} text="Create a project to start tracking work for a client." />
        ) : (
          <div className="missions">
            {d.activeProjects.map((p) => (
              <button className="mcard" key={p.id} onClick={() => nav(`/p/${p.id}`)}>
                <div className="top">
                  <div className="ms-ring" style={{ ['--p' as string]: p.progress }}><span>{Math.round(p.progress)}%</span></div>
                  <div className="nm">
                    <b data-tooltip={p.name}>{p.name}</b>
                    <small>{p.client?.name ? `${p.client.name} · ` : ''}{p.counts.total} {t.items} · {Math.round(p.progress)}%</small>
                  </div>
                </div>
                <div className="bot"><span className="ms-code">{p.code}</span><Due due={p.dueAt} /></div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="two">
        <div className="card" id="h-due" style={{ scrollMarginTop: 12 }}>
          <div className="card-h">
            <span className="tile"><CalendarClock /></span>
            <h2>Upcoming deadlines</h2>
            <span className="sp" />
            <button className="btn btn-g sm" onClick={() => nav('/my')}>{t.myItems} <ArrowRight /></button>
          </div>
          <div>
            {ld ? (
              <div style={{ padding: 16 }}><span className="ui-skel" style={{ width: '70%', display: 'block', height: 14 }} /><span className="ui-skel" style={{ width: '50%', display: 'block', height: 14, marginTop: 14 }} /></div>
            ) : !d?.upcoming.length ? (
              <div style={{ padding: '18px 16px', color: 'var(--text-3)' }}>No upcoming deadlines.</div>
            ) : (
              d.upcoming.slice(0, 6).map((m) => {
                const p = projById.get(m.projectId);
                return <MissionRow key={m.id} m={m} projectName={p?.name} projectColor={p?.color} />;
              })
            )}
          </div>
        </div>
        <div className="card" id="h-crew" style={{ scrollMarginTop: 12 }}>
          <div className="card-h">
            <span className="tile"><Users /></span>
            <h2>{t.crew} workload</h2>
            <span className="sub">open ETA</span>
          </div>
          <div className="wl">
            {!d?.workload.length ? (
              <div style={{ padding: '12px 0', color: 'var(--text-3)' }}>{ld ? 'Loading…' : 'No open work.'}</div>
            ) : (
              d.workload.slice(0, 6).map((w) => {
                const h = Math.round(w.plannedMinutes / 60);
                return (
                  <div className="r" key={w.user.id}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <Av u={w.user} size={24} />
                      <span className="ui-truncate">{w.user.name}</span>
                    </span>
                    <span className="bar" data-tooltip={`${w.open} open · ${w.overdue} overdue`}>
                      <i style={{ width: `${Math.round((w.plannedMinutes / maxLoad) * 100)}%`, background: h > 32 ? 'var(--danger)' : h > 20 ? '#e59400' : 'var(--brand)' }} />
                    </span>
                    <b style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>{h}h</b>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div className="card" id="h-notif" style={{ marginTop: 16, scrollMarginTop: 12 }}>
        <div className="card-h">
          <span className="tile"><Bell /></span>
          <h2>Latest updates</h2>
          <span className="sp" />
          <button className="btn btn-g sm" onClick={() => nav('/inbox')}>Inbox <ArrowRight /></button>
        </div>
        <NotificationFeed tab="all" compact limit={4} />
      </div>
    </div>
  );
}

